import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Check, Clock, ChevronRight } from 'lucide-react';
import { useNotifications, NOTIFICATION_CATEGORIES } from '../../context/NotificationContext';

// Get relative time string
const getRelativeTime = (timestamp) => {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
};

// Get icon for notification type
const getNotificationIcon = (category) => {
  switch (category) {
    case 'COMPLETION':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'UPLOAD':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      );
    case 'APPROVAL':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'COLLABORATION':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      );
    case 'DEADLINE':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'PROGRESS':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      );
    case 'SOULFOOD':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      );
    default:
      return <Bell className="w-4 h-4" />;
  }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { notifications, getUnreadCount, markAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const unreadCount = getUnreadCount();
  const recentNotifications = notifications.slice(0, 10);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setIsOpen(false);
    if (notification.actions && notification.actions[0]?.navigate) {
      navigate(notification.actions[0].navigate);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 rounded-xl transition-all duration-200"
        style={{ 
          backgroundColor: isOpen ? '#F1F5F9' : 'transparent',
          color: '#64748B'
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = '#FFFFFF';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white rounded-full px-1"
            style={{ backgroundColor: '#EF4444' }}
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div 
          className="absolute right-0 top-full mt-2 w-96 rounded-xl overflow-hidden"
          style={{ 
            backgroundColor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            boxShadow: '0 10px 40px rgba(0,0,0,0.12)'
          }}
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div 
            className="px-4 py-3 flex items-center justify-between"
            style={{ borderBottom: '1px solid #E2E8F0' }}
          >
            <h3 className="font-semibold text-sm" style={{ color: '#0F172A' }}>
              Notifications
            </h3>
            {unreadCount > 0 && (
              <span 
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}
              >
                {unreadCount} new
              </span>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-[400px] overflow-y-auto">
            {recentNotifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-8 h-8 mx-auto mb-2" style={{ color: '#94A3B8' }} />
                <p className="text-sm" style={{ color: '#64748B' }}>No notifications yet</p>
              </div>
            ) : (
              recentNotifications.map((notification) => {
                const categoryInfo = NOTIFICATION_CATEGORIES[notification.category];
                return (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="w-full px-4 py-3 flex items-start gap-3 text-left transition-all hover:bg-gray-50"
                    style={{ 
                      backgroundColor: notification.read ? 'transparent' : 'rgba(0, 201, 167, 0.04)',
                      borderBottom: '1px solid #F1F5F9'
                    }}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    {/* Unread indicator */}
                    <div className="flex-shrink-0 pt-1">
                      {!notification.read && (
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: '#00C9A7' }}
                        />
                      )}
                      {notification.read && <div className="w-2 h-2" />}
                    </div>
                    
                    {/* Icon */}
                    <div 
                      className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ 
                        backgroundColor: categoryInfo?.bgColor || '#F1F5F9',
                        color: categoryInfo?.color || '#64748B'
                      }}
                    >
                      {getNotificationIcon(notification.category)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p 
                        className="text-sm font-medium truncate"
                        style={{ color: '#0F172A' }}
                      >
                        {notification.headline}
                      </p>
                      <p 
                        className="text-xs mt-0.5 flex items-center gap-1"
                        style={{ color: '#94A3B8' }}
                      >
                        <Clock className="w-3 h-3" />
                        {getRelativeTime(notification.timestamp)}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <button
            onClick={() => {
              setIsOpen(false);
              navigate('/inbox');
            }}
            className="w-full px-4 py-3 flex items-center justify-center gap-2 transition-all hover:bg-gray-50"
            style={{ borderTop: '1px solid #E2E8F0', color: '#00C9A7' }}
            data-testid="view-all-inbox"
          >
            <span className="text-sm font-medium">View All in Inbox</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
